/**
 * Super Admin Company Subscription Service
 * Handles company subscription-related API calls for super administrators
 */
import API_CONFIG from '../../config';
import axios from 'axios';
import { getSuperAdminAuthHeader } from './superAdminAuthService';

const API_URL = API_CONFIG.BASE_URL;

/**
 * Activate subscription for a company
 * @param {Object} activationData - Activation data
 * @param {number} activationData.companyId - Company ID
 * @param {string} activationData.billingCycle - 'monthly' or 'yearly'
 * @param {number} activationData.amountPaid - Amount paid
 * @param {string} activationData.currency - Currency code (INR, USD, etc.)
 * @param {string} activationData.paymentStatus - 'paid', 'pending', 'partial'
 * @param {string} activationData.paymentDate - Payment date (ISO string)
 * @param {string} activationData.paymentReference - Payment reference number
 * @param {string} activationData.notes - Additional notes
 * @returns {Promise} Promise with activation response
 */
export const activateSubscription = async (activationData) => {
  try {
    const response = await axios.post(
      `${API_URL}/company-subscriptions/activate`,
      activationData,
      {
        headers: getSuperAdminAuthHeader()
      }
    );

    return response.data;
  } catch (error) {
    console.error('Error activating subscription:', error);
    if (error.response) {
      const message = error.response.data?.message || 'Failed to activate subscription';
      const status = error.response.status;

      switch (status) {
        case 401:
          throw new Error('Unauthorized. Please login again.');
        case 403:
          throw new Error('Access forbidden. You do not have permission to activate subscriptions.');
        case 404:
          throw new Error('Company or subscription not found.');
        case 400:
          throw new Error(message || 'Invalid request data.');
        case 500:
          throw new Error('Server error. Please try again later.');
        default:
          throw new Error(message || `Failed to activate subscription (${status})`);
      }
    } else if (error.request) {
      throw new Error('Network error: Server is unavailable. Please try again or contact support.');
    } else {
      throw new Error('Error: ' + error.message);
    }
  }
};

/**
 * Get company subscription details
 * @param {number} companyId - Company ID
 * @returns {Promise} Promise with subscription data
 */
export const getCompanySubscription = async (companyId) => {
  try {
    // This endpoint might need to be created, for now we'll use a placeholder
    const response = await axios.get(
      `${API_URL}/company-subscriptions/company/${companyId}`,
      {
        headers: getSuperAdminAuthHeader()
      }
    );

    return response.data;
  } catch (error) {
    console.error('Error fetching company subscription:', error);
    throw error;
  }
};

/**
 * Deactivate subscription for a company (Super Admin only)
 * @param {number} companyId - Company ID
 * @returns {Promise} Promise with deactivation response
 */
export const deactivateSubscription = async (companyId) => {
  try {
    const response = await axios.post(
      `${API_URL}/company-subscriptions/deactivate/${companyId}`,
      {},
      {
        headers: getSuperAdminAuthHeader()
      }
    );

    return response.data;
  } catch (error) {
    console.error('Error deactivating subscription:', error);
    if (error.response) {
      const message = error.response.data?.message || 'Failed to deactivate subscription';
      const status = error.response.status;

      switch (status) {
        case 401:
          throw new Error('Unauthorized. Please login again.');
        case 403:
          throw new Error('Access forbidden. You do not have permission to deactivate subscriptions.');
        case 404:
          throw new Error('Company or subscription not found.');
        case 400:
          throw new Error(message || 'Invalid request data.');
        case 500:
          throw new Error('Server error. Please try again later.');
        default:
          throw new Error(message || `Failed to deactivate subscription (${status})`);
      }
    } else if (error.request) {
      throw new Error('Network error: Server is unavailable. Please try again or contact support.');
    } else {
      throw new Error('Error: ' + error.message);
    }
  }
};

/**
 * Reactivate a deactivated subscription (Super Admin only)
 * @param {number} companyId - Company ID
 * @returns {Promise} Promise with reactivation response
 */
export const reactivateSubscription = async (companyId) => {
  try {
    const response = await axios.post(
      `${API_URL}/company-subscriptions/reactivate/${companyId}`,
      {},
      {
        headers: getSuperAdminAuthHeader()
      }
    );

    return response.data;
  } catch (error) {
    console.error('Error reactivating subscription:', error);
    if (error.response) {
      const message = error.response.data?.message || 'Failed to reactivate subscription';
      const status = error.response.status;

      switch (status) {
        case 401:
          throw new Error('Unauthorized. Please login again.');
        case 403:
          throw new Error('Access forbidden. You do not have permission to reactivate subscriptions.');
        case 404:
          throw new Error('Company or subscription not found.');
        case 400:
          throw new Error(message || 'Invalid request data.');
        case 500:
          throw new Error('Server error. Please try again later.');
        default:
          throw new Error(message || `Failed to reactivate subscription (${status})`);
      }
    } else if (error.request) {
      throw new Error('Network error: Server is unavailable. Please try again or contact support.');
    } else {
      throw new Error('Error: ' + error.message);
    }
  }
};

// Export default service object
const companySubscriptionService = {
  activateSubscription,
  getCompanySubscription,
  deactivateSubscription,
  reactivateSubscription
};

export default companySubscriptionService;

